import { useRef } from 'react'
import { cn } from '@/utlis/cn'
import { UserContextState } from '@/UserContext'
import { Video2, VideoSource } from '../../../shared/Video2'
import { isReordering, PinnedVideoInfo, sessionViewModeState, ViewMode } from '../../../shared/useViewMode'
import useScreenControlHostActions from '@/services/useScreenControlHostActions'
import { UsernameOverlay } from '../../../shared/UsernameOverlay'
import { CameraControlsOverlay } from '../../../shared/CameraControlsOverlay'
import { VideoOptionsOverlay } from '../../../shared/VideoOptionsOverlay'
import ActiveSpeakerIndicator from '../../../shared/ActiveSpeakerIndicator'
import ReactionOverlay from '../../../shared/ReactionOverlay'
import ScreenControlInputController from '@/services/ScreenControlInputController'
import { useVideoGridDragDrop } from './useVideoGridDragDrop'

type VideoGridVideoProps = {
    vid: VideoSource
    userState: UserContextState
    viewMode: ViewMode
    numVideos: number
    pinnedVideo?: PinnedVideoInfo
    index: number
}
export function VideoGridVideo({ vid, userState, viewMode, pinnedVideo, numVideos, index }: VideoGridVideoProps) {
    const {
        isControllingPeerDesktop,
        checkVideoScreenControllable,
        onRemoteDesktopControlEvent,
        videoAreaClickHandler,
    } = useScreenControlHostActions()

    const sid = vid.isMe ? userState.sessionId : vid.peer!.sessionId
    const userUrn = vid.isMe ? userState.userUrn : vid.peer!.userUrn
    let roundedClass = numVideos >= 10 ? 'rounded-[16px]' : 'rounded-[32px]'
    const isPinned = pinnedVideo?.peerSessionId === sid && pinnedVideo?.kind === vid.kind
    if (isPinned && vid.kind === 'screen') {
        roundedClass = 'rounded-[8px]'
    }
    if (isPinned && viewMode.fullscreen) {
        roundedClass = 'rounded-0'
    }
    const viewingPinnedPresentation = isPinned && vid.kind === 'screen'
    const { isDragging, isOver, dragDropRef } = useVideoGridDragDrop(
        vid.peer?.sessionId ?? userState.sessionId,
        index,
        (from: number, to: number) => {
            sessionViewModeState.moveVideo(from, to)
        },
        isReordering(viewMode)
    )

    const renderOverlays = () => {
        if (viewingPinnedPresentation) {
            return null
        }

        return (
            <>
                <UsernameOverlay peer={vid.peer ?? 'me'} kind={vid.kind} />
                {vid.kind === 'camera' && <CameraControlsOverlay peer={vid.peer ?? 'me'} />}
                <VideoOptionsOverlay peer={vid.peer ?? 'me'} pinned={isPinned} kind={vid.kind} />
                <ActiveSpeakerIndicator participantSessionId={sid} className={cn('inset-1', roundedClass)} />
                <ReactionOverlay userUrn={userUrn} />
            </>
        )
    }
    const overlays = renderOverlays()

    const videoContainerRef = useRef<HTMLDivElement | null>(null)

    const allowScreenControl = checkVideoScreenControllable(vid)

    const stream: MediaStream | null = (() => {
        if ('media' in vid && vid.media && 'stream' in vid.media) {
            return vid.media.stream
        }
        return null
    })()

    const registerScreenControl = allowScreenControl && videoContainerRef.current && stream

    return (
        <div
            key={vid.id}
            ref={(el) => {
                if (el) {
                    videoContainerRef.current = el
                    dragDropRef(el)
                }
            }}
            className={cn(
                'video-container relative group',
                viewMode.fullscreen && !!pinnedVideo ? 'h-full' : 'aspect-[1.5] p-1'
            )}
        >
            {overlays}
            <Video2
                key={`${vid.id}_registerScreenControl=${registerScreenControl}`}
                videoSource={vid}
                ignoreAspectRatio
                tabIndex={registerScreenControl ? 0 : undefined}
                className={cn(
                    'h-full w-full',
                    isReordering(viewMode) && 'border-muse-yellow border-2 border-dashed cursor-move',
                    roundedClass,
                    isPinned && vid.kind === 'screen' ? 'object-fit' : 'object-cover'
                )}
            />

            {registerScreenControl && (
                <ScreenControlInputController
                    srcObject={stream}
                    videoContainerEl={videoContainerRef.current}
                    onRemoteDesktopControlEvent={isControllingPeerDesktop ? onRemoteDesktopControlEvent : undefined}
                    onVideoAreaClick={() => videoAreaClickHandler(vid)}
                />
            )}
        </div>
    )
}
